<template>
  <div class="modal-container" data-testid="add-request-content-modal-wrapper">
    <div class="modal-inner">
      <!-- Header for form view only -->
      <div v-if="currentView === MODAL_VIEWS.FORM && !showCollectionPicker" class="modal-header">
        <h2 class="modal-title">{{ MODAL_TITLES.CREATE_REQUEST_LINK }}</h2>
      </div>

      <!-- Vue Transition for smooth content switching -->
      <Transition name="modal-content" mode="out-in">
        <div :key="currentViewKey" class="content-container">
          <!-- Form view -->
          <RequestContentForm
            v-if="currentView === MODAL_VIEWS.FORM && !showCollectionPicker"
            :edit="false"
            :item="null"
            :domain="props.domain"
            :selected-collection="selectedCollection"
            @save="handleCreateFormSave"
            @close="onCloseHandler"
            @show-collection-picker="openCollectionPicker"
          />

          <!-- Success view -->
          <RequestContentSuccess
            v-else-if="currentView === MODAL_VIEWS.SUCCESS && savedItem && !showCollectionPicker"
            :item="savedItem"
            :domain="props.domain"
            @close="onCloseHandler"
            @edit-url="showEditUrl"
          />

          <!-- Edit URL view -->
          <RequestContentEditUrl
            v-else-if="currentView === MODAL_VIEWS.EDIT_URL && savedItem && !showCollectionPicker"
            :item="savedItem"
            :domain="props.domain"
            @close="showPreviousView"
            @save="handleUrlSave"
          />

          <!-- Collection picker view -->
          <CollectionPicker
            v-else-if="showCollectionPicker"
            :selected-id="selectedCollectionId"
            @select="handleCollectionSelect"
            @cancel="closeCollectionPicker"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { RequestContent } from '../../types';
import CollectionPicker from '@/components/CollectionPicker.vue';
import RequestContentForm from './RequestContentForm.vue';
import RequestContentSuccess from './RequestContentSuccess.vue';
import RequestContentEditUrl from './RequestContentEditUrl.vue';
import { useRequestContentModalShared } from '../../composables/useRequestContentModalShared';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useToast } from '@/composables/useToast';
import { MODAL_VIEWS, MODAL_TITLES } from '../../constants/constants';

interface Props {
  domain: string;
}

const props = defineProps<Props>();

interface Emits {
  close: [];
  save: [item: RequestContent];
}

const emit = defineEmits<Emits>();

const requestContentStore = useRequestContentStore();
const { error } = useToast();

// Use shared modal logic
const {
  currentView,
  savedItem,
  showCollectionPicker,
  selectedCollectionId,
  selectedCollection,
  onCloseHandler,
  handleUrlSave,
  showEditUrl,
  showPreviousView,
  handleCollectionSelect,
  closeCollectionPicker,
  openCollectionPicker,
} = useRequestContentModalShared({ onClose: () => emit('close') });

// Computed key for transition
const currentViewKey = computed(() => {
  if (showCollectionPicker.value) return 'collection-picker';
  return currentView.value;
});

const handleCreateFormSave = async (formData: any) => {
  try {
    const createdItem = await requestContentStore.createRequestContent(formData);
    savedItem.value = createdItem;

    if (createdItem.requestLink) {
      try {
        await navigator.clipboard.writeText(createdItem.requestLink);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }

    emit('save', createdItem);
    currentView.value = MODAL_VIEWS.SUCCESS;
  } catch (apiError) {
    console.error('Failed to create request content:', apiError);
    error('Failed to create request link. Please try again.');
  }
};
</script>

<style scoped>
.modal-container {
  @apply w-full md:w-128 pt-16 sm:pt-5;
}

.modal-inner {
  @apply relative;
  min-height: 320px; /* Reduced height for better fit */
}

.content-container {
  @apply w-full;
  min-height: inherit;
}

.modal-header {
  @apply flex items-center justify-between mb-6 border-b border-zinc-200 dark:border-zinc-700 pb-4 pr-12;
}

.modal-title {
  @apply text-xl font-semibold text-zinc-900 dark:text-zinc-100;
}

/* Vue transition classes */
.modal-content-enter-active,
.modal-content-leave-active {
  transition: all 0.3s ease-in-out;
}

.modal-content-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.modal-content-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.modal-content-enter-to,
.modal-content-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
